// bt-common/engine/sender-binding.ts
// #922 S0b: sender binding (plan §2.3-2.6). Shared types + PURE helpers; the backend owns the I/O
// (the wa_senders registry, engineId → sessions.phoneNumber, the slot gate). Behind
// SENDER_BINDING_MODE = off|observe|enforce (default observe): observe only reports a would-mismatch,
// it never blocks and never changes routing. With one sender per owner (N<=1) every verdict is a match:
// the only sender counts as the default even before `defaultSenderId` is set, and an engine the registry
// cannot place (unclaimed, or a number not registered yet) may claim exactly the docs a pre-S0b build may.
import type { EngineFamily, EngineKind } from './engine-kind';
import type { EngineId, SenderId } from './identity';

// ── flag ────────────────────────────────────────────────────────────────────

export const SENDER_BINDING_MODES = ['off', 'observe', 'enforce'] as const;
export type SenderBindingMode = (typeof SENDER_BINDING_MODES)[number];
export const DEFAULT_SENDER_BINDING_MODE: SenderBindingMode = 'observe';

const MODE_STRINGS: readonly string[] = SENDER_BINDING_MODES;

function isSenderBindingMode(v: string): v is SenderBindingMode {
    return MODE_STRINGS.includes(v);
}

/** Reads the flag value (e.g. `process.env.SENDER_BINDING_MODE`). Absent or unrecognised → `observe`:
 *  a typo must never switch enforcement on, and must not silently switch observation off either. */
export function parseSenderBindingMode(raw: string | undefined): SenderBindingMode {
    const v = (raw ?? '').trim().toLowerCase();
    return isSenderBindingMode(v) ? v : DEFAULT_SENDER_BINDING_MODE;
}

/** #1155 S-MN1 step 1: the mode for one owner. A multi-number owner (`maxNumbers > 1`) is always enforced:
 *  until push is sender-targeted, a doc can reach an engine on another of the owner's Numbers, and only the
 *  deny keeps it from going out from the wrong number. One-number owners keep the global flag. `off` stays a
 *  real kill switch (the gate does no I/O at all), so it is never upgraded. Pure. */
export function ownerSenderBindingMode(global: SenderBindingMode, maxNumbers: number): SenderBindingMode {
    if (global === 'off') return 'off';
    return maxNumbers > 1 ? 'enforce' : global;
}

// ── shared shapes ───────────────────────────────────────────────────────────

/** A `wa_senders` doc: one WhatsApp number an account sends from / receives on. Unique (owner, phoneNumber). */
export interface WaSender {
    _id: SenderId;
    owner: string;
    /** E.164 digits only, no `+` (see {@link normalizeSenderPhone}). */
    phoneNumber: string;
    family: EngineFamily;
    label?: string;
    /** The kind of the engine whose connect created the row. */
    createdFrom: EngineKind;
    createdAt: Date;
}

/** What an engine says about itself on a slot / can-i-send request and on its log rows. Both fields are
 *  optional: builds that predate S0b send neither, and the server treats that as an "unclaimed" request
 *  (plan §2.4 old-build rule). `senderPhone` is only trusted for `client:` engines (the local extension
 *  has no sessions doc); for `session:` engines the server reads the number from the sessions doc. */
export interface EngineSenderClaim {
    engineId?: EngineId;
    senderPhone?: string;
}

/** The claim as body fields, without empty values, for a conditional spread: a request from an engine
 *  that carries no claim is byte-identical to the pre-S0b body. */
export function senderClaimFields(claim: EngineSenderClaim): EngineSenderClaim {
    const out: EngineSenderClaim = {};
    if (claim.engineId) out.engineId = claim.engineId;
    if (claim.senderPhone) out.senderPhone = claim.senderPhone;
    return out;
}

/** Which of the account's numbers (and which engine instance) an inbound event arrived on. Optional on
 *  the v2 envelope: when absent the backend fills `phoneNumber` from `msg.to`. `engineId` is absent on a
 *  backend-filled value, because an old build's envelope does not say which engine published it. */
export interface ReceivedOn {
    senderId?: SenderId;
    engineId?: EngineId;
    phoneNumber: string;
}

const MIN_PHONE_DIGITS = 7;
const MAX_PHONE_DIGITS = 15; // E.164

/** A sender phone as E.164 digits (no `+`), from a bare number, `+972 54-…`, `972…@c.us`,
 *  `972…:12@s.whatsapp.net` / `972….0:12@s.whatsapp.net` (agent and device suffixes dropped).
 *  `@lid` / `@g.us` / other non-phone ids → null. */
export function normalizeSenderPhone(raw: string): string | null {
    const at = raw.indexOf('@');
    const server = at >= 0 ? raw.slice(at + 1) : '';
    if (at >= 0 && server !== 'c.us' && server !== 's.whatsapp.net') return null;
    // JID user part is `<number>[.<agent>][:<device>]`. Only in JID form: a bare `054.123.4567` keeps its dots.
    const user = at >= 0 ? raw.slice(0, at).split(':')[0].split('.')[0] : raw.split(':')[0];
    if (/[a-z]/i.test(user)) return null;
    const digits = user.replace(/\D/g, '');
    return digits.length >= MIN_PHONE_DIGITS && digits.length <= MAX_PHONE_DIGITS ? digits : null;
}

// ── create-time stamping (plan §2.5) ────────────────────────────────────────

export type SenderResolutionVia = 'explicit' | 'parent' | 'chat' | 'only' | 'default';

export type SenderResolution =
    | { ok: true; senderId: SenderId; via: SenderResolutionVia }
    /** `no_senders`: the owner has no registry rows yet → leave the doc unset (= today's behaviour).
     *  `sender_unknown`: an explicit/parent/chat id is not one of the owner's senders.
     *  `sender_ambiguous`: several senders and nothing picks one; never guess. */
    | { ok: false; code: 'no_senders' | 'sender_unknown' | 'sender_ambiguous' };

export interface SenderResolutionInput {
    /** The owner's registered senders. */
    senders: readonly SenderId[];
    defaultSenderId: SenderId | null;
    /** The message's own pick (UI picker, `/v1`/MCP `from` already mapped to an id). */
    explicit?: SenderId;
    /** The campaign / series / drip the doc is minted from. */
    parent?: SenderId;
    /** `chat_sender_bindings` row for the contact (lands later; accepted now so callers need no reshape). */
    chat?: SenderId;
}

/** Precedence: explicit > parent > chat > the only sender > the account default. Pure. */
export function resolveSender(input: SenderResolutionInput): SenderResolution {
    const { senders } = input;
    if (senders.length === 0) return { ok: false, code: 'no_senders' };
    const known = (id: SenderId): boolean => senders.includes(id);
    const picks: Array<[SenderId | undefined, 'explicit' | 'parent' | 'chat']> = [
        [input.explicit, 'explicit'], [input.parent, 'parent'], [input.chat, 'chat'],
    ];
    for (const [id, via] of picks) {
        if (id === undefined) continue;
        return known(id) ? { ok: true, senderId: id, via } : { ok: false, code: 'sender_unknown' };
    }
    if (senders.length === 1) return { ok: true, senderId: senders[0], via: 'only' };
    if (input.defaultSenderId !== null && known(input.defaultSenderId)) {
        return { ok: true, senderId: input.defaultSenderId, via: 'default' };
    }
    return { ok: false, code: 'sender_ambiguous' };
}

// ── claim-time check (plan §2.4) ────────────────────────────────────────────

/** The server's reading of the requesting engine's sender. */
export type EngineSenderResolution =
    /** The engine's number maps to one of the owner's senders. */
    | { status: 'resolved'; senderId: SenderId }
    /** The request carried no claim (a pre-S0b build), or a `session:` claim whose session has no phone
     *  number yet: there is nothing to compare, so it is judged like a pre-S0b build. */
    | { status: 'unclaimed' }
    /** A number is known but is not a registered sender of this owner (or the engine id is malformed). */
    | { status: 'unknown' };

export type SenderMismatchReason =
    /** The engine is on a different one of the owner's numbers. */
    | 'other_sender'
    /** A pre-S0b build asked for a doc bound to a non-default sender. */
    | 'legacy_build_non_default'
    /** The engine's number is not a registered sender of this owner. */
    | 'engine_sender_unknown';

export interface SenderMismatch {
    reason: SenderMismatchReason;
    /** The doc's effective sender (its `senderId`, else the owner default). */
    docSenderId: SenderId;
    engineSenderId: SenderId | null;
}

export type SenderMatchVia =
    /** The doc is bound and the engine is on that sender. */
    | 'bound'
    /** The doc is unset and the engine is on the owner default. */
    | 'default'
    /** A pre-S0b build and the doc's effective sender is the owner default. */
    | 'legacy_build_default'
    /** The engine's number is not registered (yet) and the doc's effective sender is the owner default:
     *  same rights as a pre-S0b build, so a registry that lags a connect never blocks a send. */
    | 'unknown_engine_default'
    /** The owner has no registry yet (no default, unset doc): nothing to compare, today's behaviour. */
    | 'no_registry';

export type SenderBindingVerdict =
    | { allow: true; outcome: 'skipped' }
    | { allow: true; outcome: 'match'; via: SenderMatchVia }
    | { allow: true; outcome: 'would_mismatch'; mismatch: SenderMismatch }
    | { allow: false; outcome: 'mismatch'; code: 'not_my_sender'; mismatch: SenderMismatch };

export interface SenderBindingFacts {
    mode: SenderBindingMode;
    engine: EngineSenderResolution;
    /** The doc's `senderId`; null = unset = "the owner's legacy default sender". */
    docSenderId: SenderId | null;
    ownerDefaultSenderId: SenderId | null;
    /** The owner's registered senders. With exactly one and no default set, that one is the default. */
    ownerSenders: readonly SenderId[];
}

/** The owner's effective default: `defaultSenderId`, else the only sender when there is exactly one. */
export function effectiveDefaultSender(facts: Pick<SenderBindingFacts, 'ownerDefaultSenderId' | 'ownerSenders'>): SenderId | null {
    if (facts.ownerDefaultSenderId !== null) return facts.ownerDefaultSenderId;
    return facts.ownerSenders.length === 1 ? facts.ownerSenders[0] : null;
}

/** May this engine claim this doc? Pure. `off` → skipped; `observe` → always allowed, a mismatch is
 *  only reported; `enforce` → a mismatch is denied with `not_my_sender`. */
export function evaluateSenderBinding(facts: SenderBindingFacts): SenderBindingVerdict {
    if (facts.mode === 'off') return { allow: true, outcome: 'skipped' };
    const ownerDefault = effectiveDefaultSender(facts);
    const effective = facts.docSenderId ?? ownerDefault;
    if (effective === null) return { allow: true, outcome: 'match', via: 'no_registry' };

    let mismatch: SenderMismatch | null = null;
    let via: SenderMatchVia = 'bound';
    const { engine } = facts;
    if (engine.status === 'unclaimed') {
        if (effective === ownerDefault) via = 'legacy_build_default';
        else mismatch = { reason: 'legacy_build_non_default', docSenderId: effective, engineSenderId: null };
    } else if (engine.status === 'unknown') {
        if (effective === ownerDefault) via = 'unknown_engine_default';
        else mismatch = { reason: 'engine_sender_unknown', docSenderId: effective, engineSenderId: null };
    } else if (engine.senderId === effective) {
        via = facts.docSenderId === null ? 'default' : 'bound';
    } else {
        mismatch = { reason: 'other_sender', docSenderId: effective, engineSenderId: engine.senderId };
    }

    if (mismatch === null) return { allow: true, outcome: 'match', via };
    return facts.mode === 'enforce'
        ? { allow: false, outcome: 'mismatch', code: 'not_my_sender', mismatch }
        : { allow: true, outcome: 'would_mismatch', mismatch };
}

export interface SenderBindingLogContext {
    /** The gate that evaluated, e.g. `acquire-send-slot` / `can-i-send`. */
    gate: string;
    owner: string;
    messageId: string;
    engineId?: EngineId;
}

/** The one log line for a mismatch, so every gate emits the same greppable shape. Ids only: no phone
 *  numbers, no message content. Observe → `[sender-binding] would-mismatch …`, enforce → `… denied …`. */
export function formatSenderMismatchLine(
    ctx: SenderBindingLogContext,
    verdict: Extract<SenderBindingVerdict, { mismatch: SenderMismatch }>,
): string {
    const m = verdict.mismatch;
    const tag = verdict.outcome === 'would_mismatch' ? 'would-mismatch' : 'denied';
    return `[sender-binding] ${tag} gate=${ctx.gate} owner=${ctx.owner} messageId=${ctx.messageId}` +
        ` engineId=${ctx.engineId ?? 'none'} reason=${m.reason} docSender=${m.docSenderId}` +
        ` engineSender=${m.engineSenderId ?? 'none'}`;
}
